import React, { useCallback, useMemo, useRef } from 'react'
import { Active, useDndMonitor, useDraggable } from '@dnd-kit/core'
import { faArrowsUpDownLeftRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { alpha, Box, styled } from '@mui/material'
import { ClearContainer } from './components/ClearContainer'
import { Coordinates } from '@dnd-kit/utilities'
import { ResizableArea } from './components'
import { CornerResizeHandle } from './components/CornerResizeHandle'
import { Resizable } from 're-resizable'
import { DraggableContainer } from './components/DraggableContainer'
import { BottomHandleStyles, BottomRightHandleStyles } from './constants'
import { ScreenMaskState, updateScreenMaskDimensions } from '@app/storage/screenMask'
import { useSnapshot } from 'valtio'

type Props = {
  getWrapperElement: () => HTMLDivElement
}

const DRAGGABLE_SCREEN_MASK_ID = 'draggable-screen-mask-area'

export type Dimensions = Coordinates & {
  width?: number
  height?: number
}

export const ScreenMaskArea: React.FC<Props> = ({ getWrapperElement }) => {
  const { dimensions } = useSnapshot(ScreenMaskState)

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: DRAGGABLE_SCREEN_MASK_ID,
  })

  const clearAreaWrapperRef = useRef(null)

  const onDragEnd = (active: Active, delta: Coordinates) => {
    if (active.id !== DRAGGABLE_SCREEN_MASK_ID) return

    updateScreenMaskDimensions({
      x: dimensions.x + delta.x,
      y: dimensions.y + delta.y,
    })
  }

  useDndMonitor({
    onDragEnd: ({ delta, active }) => onDragEnd(active, delta),
  })

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined

  const minWidth = useMemo(() => {
    // 24 is the sum of margins.
    const xBound = dimensions.clearAreaWidth + 24 + dimensions.clearAreaX
    if (xBound > 300) return xBound

    return 300
  }, [dimensions.clearAreaWidth, dimensions.clearAreaX])

  const minHeight = useMemo(() => {
    // 24 is the sum of margins. 20 is the heights of top and bottom bars.
    const yBound = dimensions.clearAreaHeight + 24 + 20 * 2 + dimensions.clearAreaY
    if (yBound > 100) return yBound

    return 100
  }, [dimensions.clearAreaHeight, dimensions.clearAreaY])

  const onResize = useCallback((dimensionsUpdate: Dimensions) => {
    updateScreenMaskDimensions({
      clearAreaWidth: dimensionsUpdate.width,
      clearAreaHeight: dimensionsUpdate.height,
      clearAreaX: dimensionsUpdate.x,
      clearAreaY: dimensionsUpdate.y,
    })
  }, [])

  const wrapperElement = getWrapperElement()
  // a11y: the mask starts at x/y inside the wrapper, so the reachable max is the space left from there.
  const maxWidth = Math.max(dimensions.width, (wrapperElement?.clientWidth ?? 0) - dimensions.x)
  const maxHeight = Math.max(dimensions.height, (wrapperElement?.clientHeight ?? 0) - dimensions.y)

  return (
    <StyledContainer ref={setNodeRef} style={{ ...style, top: dimensions.y, left: dimensions.x }}>
      <DraggableContainer
        {...attributes}
        {...listeners}
        // a11y: application role keeps NVDA in key-passing mode so Space picks up and arrows drag; button role leaves NVDA in browse mode.
        role='application'
        aria-label='Drag mask area'
      >
        <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
      </DraggableContainer>
      <StyledResizableBox
        size={{
          width: dimensions.width,
          height: dimensions.height,
        }}
        onResizeStop={(_e, _direction, _ref, delta) => {
          updateScreenMaskDimensions({
            width: dimensions.width + delta.width,
            height: dimensions.height + delta.height,
          })
        }}
        enable={{
          top: false,
          left: false,
          right: false,
          topRight: false,
          topLeft: false,
          bottomLeft: false,
          // a11y: list bottom before bottomRight so the height handle tabs before the corner (reading order).
          bottom: true,
          bottomRight: true,
        }}
        minWidth={minWidth}
        minHeight={minHeight}
        handleComponent={{
          bottom: (
            <ResizableArea
              label='Resize mask height'
              value={dimensions.height}
              min={minHeight}
              max={maxHeight}
              onResize={delta =>
                updateScreenMaskDimensions({ height: Math.max(dimensions.height + delta, minHeight) })
              }
            />
          ),
          bottomRight: (
            <CornerResizeHandle
              label='Resize mask width and height'
              width={dimensions.width}
              height={dimensions.height}
              minWidth={minWidth}
              minHeight={minHeight}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              onResize={(wDelta, hDelta) =>
                updateScreenMaskDimensions({
                  width: Math.max(dimensions.width + wDelta, minWidth),
                  height: Math.max(dimensions.height + hDelta, minHeight),
                })
              }
            />
          ),
        }}
        handleStyles={{
          bottomRight: BottomRightHandleStyles,
          bottom: BottomHandleStyles,
        }}
        bounds={wrapperElement}
      >
        <StyledBox ref={clearAreaWrapperRef}>
          <ClearContainer getParentElement={() => clearAreaWrapperRef.current} onResize={onResize} />
        </StyledBox>
      </StyledResizableBox>
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  height: 100,
  minWidth: '300px',
  minHeight: '100px',
}))

const StyledBox = styled(Box)(({ theme }) => ({
  position: 'relative',
  margin: theme.spacing(3),
  boxShadow: `0 0 0 ${theme.spacing(3)} ${alpha('#636363', 0.75)}`,
  overflow: 'hidden',
  height: 'calc(100% - 20px)',
  flexGrow: 1,
}))

const StyledResizableBox = styled(Resizable)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
}))
